/**
 * useSession — Dashboard access gate
 *
 * - Verifies a passphrase against its SHA-256 hash using WebCrypto (no library needed)
 * - Stores unlock state in sessionStorage (clears on tab close)
 * - Auto-locks after INACTIVITY_MS of no user interaction
 *
 * NOTE: This is a client-side access gate. The hash is in the bundle.
 * For production medical deployments use a server-side session + JWT.
 * Default passphrase: SmartPatch@01
 */
import { useState, useEffect, useCallback, useRef } from 'react';

const INACTIVITY_MS  = 5 * 60 * 1000;          // 5 minutes
const SESSION_KEY    = 'sp_session_unlocked';

// SHA-256 of "SmartPatch@01"  — change by running: hashPassphrase('yourpass').then(console.log)
const CORRECT_HASH   = 'efc73bc1462d22d9b5c1e41a487b9cb16aa003beec2c22d325224bb6eec71762';

export async function hashPassphrase(pass) {
  const buf  = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(pass));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

export async function verifyPassphrase(pass) {
  const hash = await hashPassphrase(pass);
  return hash === CORRECT_HASH;
}

export default function useSession() {
  const [unlocked, setUnlocked] = useState(() =>
    sessionStorage.getItem(SESSION_KEY) === '1'
  );
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  // Reset inactivity timer on any user event
  const resetTimer = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setUnlocked(false);
      sessionStorage.removeItem(SESSION_KEY);
    }, INACTIVITY_MS);
  }, []);

  useEffect(() => {
    if (!unlocked) return;
    const events = ['mousemove', 'keydown', 'pointerdown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      clearTimeout(timerRef.current);
    };
  }, [unlocked, resetTimer]);

  const login = useCallback(async (pass) => {
    setLoading(true);
    setError('');
    // Artificial 600ms delay to deter brute-force
    await new Promise(r => setTimeout(r, 600));
    const ok = await verifyPassphrase(pass);
    if (ok) {
      sessionStorage.setItem(SESSION_KEY, '1');
      setUnlocked(true);
    } else {
      setError('Incorrect passphrase. Access denied.');
    }
    setLoading(false);
  }, []);

  const logout = useCallback(() => {
    clearTimeout(timerRef.current);
    sessionStorage.removeItem(SESSION_KEY);
    setUnlocked(false);
    setError('');
  }, []);

  return { unlocked, login, logout, error, loading };
}
